<template>
  <div class="cmr00200-wrapper">
    <PageHeaderBanner>
      <template #title>新規管理者登録</template>
    </PageHeaderBanner>
    <q-form autofocus greedy novalidate @submit.prevent="isConfimationFlg = true">
      <div class="mx-auto cmr00200-form-area">
        <MessageChip
          :display="isMessageChip"
          level="E"
          :message="errMessage"
          @validation-error="resetError"
        />
        <div class="col q-gutter-sm q-mb-sm">
          <label for="name" class="requier">お名前（姓）</label>
          <q-input
            v-model="userNameSei"
            outlined
            maxlength="100"
            dense
            :rules="[
              (val) => isEmpty(val) || $t('validation.required'),
              (val) => !isHalfKana(val) || $t('validation.half_kana'),
            ]"
            lazy-rules
            class="input-area left-input"
            hint="例）山田"
            @keydown.enter.prevent
          />
        </div>
        <div class="col q-gutter-sm q-mb-sm">
          <label class="requier">お名前（名）</label>
          <q-input
            v-model="userNameMei"
            outlined
            dense
            maxlength="100"
            bg-color="white"
            :rules="[
              (val) => isEmpty(val) || $t('validation.required'),
              (val) => !isHalfKana(val) || $t('validation.half_kana'),
            ]"
            lazy-rules
            class="input-area left-input"
            hint="例）太郎"
            @keydown.enter.prevent
          />
        </div>
        <div class="col">
          <label class="requier">会員ID（メールアドレス）</label>
          <q-input
            v-model="mailAddress"
            outlined
            type="email"
            dense
            maxlength="254"
            :rules="[
              (val) => isEmpty(val) || $t('validation.required'),
              (val) => isValidateEmail(val) || $t('validation.email'),
            ]"
            lazy-rules
            class="input-area left-input"
            hint="例）example@xxx.com"
            :error="isMailAddressErr"
            @keydown.enter.prevent
          />
        </div>
        <div class="col q-gutter-sm column">
          <label>ユーザー権限</label>
          <span class="q-ml-md">管理者</span>
        </div>
      </div>
      <FormFooterBanner style="position: inherit">
        <template #button-area>
          <div>
            <q-btn
              color="primary"
              outline
              style="width: 120px"
              class="q-mr-xs"
              icon="mdi-chevron-left"
              label="戻る"
              :to="{ name: 'members' }"
            />
            <q-btn type="submit" color="primary" style="width: 120px" label="登録" />
          </div>
        </template>
      </FormFooterBanner>
    </q-form>
    <AlertDialog
      :display="isConfimationFlg"
      level="information"
      title="登録確認"
      :text="['入力された内容で登録します。', 'よろしいですか？']"
      @confirm="(isConfimationFlg = false), registerAdmin()"
      @close="isConfimationFlg = false"
    />
    <AlertDialog
      :display="isAlertFlg"
      level="information"
      confirmation
      title="登録完了"
      :text="['新規管理者登録が完了しました。']"
      @complete="router.push({ name: 'members' })"
    />
  </div>
</template>
<script setup lang="ts">
import { Loading } from 'quasar';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from '../../common/axios-common';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import { isEmpty, isValidateEmail, isHalfKana } from '../../common/utils/validateUtils';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';

const router = useRouter();

// 登録確認フラグ
const isConfimationFlg = ref(false);

// アラートダイアログフラグ
const isAlertFlg = ref(false);
// エラーメッセージ
const errMessage = ref('');
// MessageChip表示フラグ
const isMessageChip = ref(false);
// ユーザー姓
const userNameSei = ref('');
// ユーザー名
const userNameMei = ref('');
// メールアドレス
const mailAddress = ref('');
// メールアドレスエラーフラグ
const isMailAddressErr = ref(false);

/**
 * エラーチップを初期化
 */
const resetError = async () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

/**
 * 会員登録
 */
const registerAdmin = async () => {
  isMailAddressErr.value = false;
  errMessage.value = '';
  isMessageChip.value = false;
  // Loading Start
  Loading.show();
  // 登録データ作成
  const formData = new FormData();
  formData.append('user_name_sei', userNameSei.value);
  formData.append('user_name_mei', userNameMei.value);
  formData.append('mail_address', mailAddress.value);

  // 管理者登録
  await axios
    .post('/cmr002001/administrator-register', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // ダイアログ表示
          isAlertFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.BAN_DOMAIN_ERROR:
            case DetailResultStatusCode.NOT_EMAIL_EXIST_ERROR:
            case DetailResultStatusCode.DUPLICATE_EMAIL_ERROR:
            case DetailResultStatusCode.PARAMETER_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              isMailAddressErr.value = true;
              break;

            // インサート、削除エラーの場合
            case DetailResultStatusCode.DELETE_ERROR:
            case DetailResultStatusCode.INSERT_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            // システムエラーの場合

            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;
            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
        // エラー画面に遷移
      } else {
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};
</script>

<style scoped>
.cmr00200-form-area {
  width: 80%;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.cmr00200-form-area .col.q-gutter-sm {
  margin-top: 5px;
}
</style>
<style>
.cmr00200-wrapper .message-chip .q-chip {
  margin-bottom: 0;
}
</style>
